<template>
  <!-- The main button hands the Smart Action output to the default agent, the chevron lists the
       agents and saves the pick as the default. -->
  <div v-if="canSendToAgent" class="send-to-agent" data-testid="summary-send-to-agent-split">
    <span class="send-to-agent__main">
      <Button
        type="solid"
        variant="gray"
        :icon="defaultIcon"
        :text="sendToDefaultLabel"
        :aria-label="sendToDefaultLabel"
        icon-only-on-mobile
        :disabled="isSending"
        data-testid="summary-send-to-agent-btn"
        @click="sendToDefault"
      >
        <template v-if="showProviderMarks" #icon>
          <span class="send-to-agent__providers" aria-hidden="true" data-testid="summary-send-to-agent-providers">
            <span v-for="mark in PROVIDER_MARKS" :key="mark" class="send-to-agent__provider">
              <Icon :name="mark" size="xs" />
            </span>
          </span>
        </template>
      </Button>
    </span>
    <ContextMenuWrapper placement="bottom-end" :edge-padding="16" test-id="summary-send-to-agent-menu">
      <span class="send-to-agent__chevron">
        <Button type="solid" variant="gray" icon="general.menuArrow" tooltip="Choose agent" data-testid="summary-send-to-agent-trigger" />
      </span>
      <template #menu>
        <li class="send-to-agent__help" data-testid="summary-send-to-agent-help">
          Send this Smart Action output to an AI agent. Your choice becomes the default.
        </li>
        <MenuLink
          v-for="option in handoffOptions"
          :key="option.agent"
          :icon="option.icon"
          :title="option.title"
          :data-testid="option.testId"
          :class="{ 'send-to-agent__option--default': option.agent === defaultAgent }"
          class="send-to-agent__option"
          @click="sendTo(option.agent)"
        >
          <span class="send-to-agent__option-text">
            <span class="send-to-agent__option-title">
              {{ option.title }}
              <Icon v-if="option.agent === defaultAgent" name="general.tick" size="sm" data-testid="summary-send-to-agent-default-check" />
            </span>
            <span class="send-to-agent__option-subtitle">{{ option.subtitle }}</span>
          </span>
        </MenuLink>
      </template>
    </ContextMenuWrapper>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Button, Icon } from '@/storybook';
import { ContextMenuWrapper, MenuLink } from '@/storybook/menu';
import { AGENT_NAMES, useAgentHandoff } from '@/modules/viewer/composables/useAgentHandoff';
import type { DefaultAgent } from '@/store/useUserStore';

const { canSendToAgent, defaultAgent, isSending, sendTo, sendToDefault } = useAgentHandoff();
const handoffOptions: Array<{ agent: DefaultAgent; title: string; subtitle: string; icon: string; testId: string }> = [
  {
    agent: 'claude',
    title: 'Send to Claude',
    subtitle: 'Opens Claude with the Smart Action output filled in.',
    icon: 'social.claude',
    testId: 'summary-send-to-claude',
  },
  {
    agent: 'chatgpt',
    title: 'Send to ChatGPT',
    subtitle: 'Opens ChatGPT with the Smart Action output filled in.',
    icon: 'social.openai',
    testId: 'summary-send-to-chatgpt',
  },
  {
    agent: 'clipboard',
    title: 'Copy for any AI tool',
    subtitle: 'Copies the Smart Action output for Cursor or another agent.',
    icon: 'general.copy',
    testId: 'summary-copy-for-ai',
  },
];
// The clipboard copy works with any AI tool, so that state shows the provider marks instead of one logo.
const PROVIDER_MARKS = ['social.claude', 'social.gemini', 'social.openai'];
const showProviderMarks = computed(() => defaultAgent.value === 'clipboard');
const defaultIcon = computed(() => (showProviderMarks.value ? undefined : handoffOptions.find(option => option.agent === defaultAgent.value)?.icon));
const sendToDefaultLabel = computed(() => (defaultAgent.value === 'clipboard' ? 'Copy for AI' : `Send to ${AGENT_NAMES[defaultAgent.value]}`));
</script>

<style scoped>
.send-to-agent {
  @apply flex items-center;
}

/* One split control: the main part and the chevron share a pill, split by a thin line. */
.send-to-agent__main :deep(.button-base) {
  @apply rounded-r-none;
}

.send-to-agent__chevron :deep(.button-base) {
  @apply rounded-l-none border-l-slate-300 dark:border-l-zinc-600 px-1.5;
}

/* Three marks on white discs, each disc a third over the one before it. The text color stays black so the currentColor ChatGPT mark shows in dark mode. */
.send-to-agent__providers {
  @apply flex shrink-0 items-center;
}

.send-to-agent__provider {
  @apply flex h-7 w-7 items-center justify-center rounded-full bg-white text-black shadow ring-1 ring-slate-900/10;
}

.send-to-agent__provider + .send-to-agent__provider {
  @apply -ml-2.5;
}

/* The menu sizes to its widest line. These caps make the text wrap so the menu fits on a phone. */
.send-to-agent__help {
  @apply px-2.5 pb-2 pt-1 text-xs font-normal text-zinc-500 dark:text-zinc-400;
  max-width: min(22rem, calc(100vw - 5rem));
}

.send-to-agent__option :deep(a) {
  @apply whitespace-normal;
}

.send-to-agent__option--default :deep(a) {
  @apply bg-slate-100 dark:bg-zinc-700 text-slate-900 dark:text-zinc-100;
}

.send-to-agent__option-text {
  @apply flex flex-col gap-0.5 whitespace-normal;
  max-width: min(20rem, calc(100vw - 7rem));
}

.send-to-agent__option-title {
  @apply flex items-center gap-1;
}

.send-to-agent__option-subtitle {
  @apply text-xs font-normal text-zinc-500 dark:text-zinc-400;
}
</style>
